import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useMemo, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextArea, TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {
    breakpoint,
    color,
    semanticColor,
    sizing,
} from "@khanacademy/wonder-blocks-tokens";
import {type Typography} from "@khanacademy/wonder-blocks-typography";
import {UsernameTextField} from "@khan/auth-password";
import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {reload} from "@khan/urls";

import {UPDATE_PROFILE_MUTATION} from "#data-access/user-profile-provider.tsx";

import globeSimpleIcon from "@phosphor-icons/core/regular/globe-simple.svg";

interface Props {
    closeModal: () => void;
    kaid: string;
    nickname: string;
    username: string;
    bio: string;
}

export default function UserCardModal(props: Props): React.ReactElement {
    const maxChars = 160;

    const initialNickname = useMemo(() => props.nickname, [props.nickname]);
    const initialUsername = useMemo(() => props.username, [props.username]);
    const initialBio = useMemo(() => props.bio, [props.bio]);

    const [nickname, setNickname] = useState(initialNickname);
    const [username, setUsername] = useState(initialUsername);
    const [bio, setBio] = useState(initialBio);

    const [usernameError, setUsernameError] = useState<string | null>(null);
    const [nicknameError, setNicknameError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [savedSuccessfully, setSavedSuccessfully] = useState(false);

    const [updateProfile] = useMutation(UPDATE_PROFILE_MUTATION);

    const hasChanges =
        nickname !== initialNickname ||
        username !== initialUsername ||
        bio !== initialBio;

    const renderUsernameDescription = (): React.ReactElement<
        React.ComponentProps<Typography>
    > => {
        // This is only an instructive link
        const profileUrl = "http://www.khanacademy.org/profile/";
        // We have to use spans here because the UsernameTextField's description
        // places its contents into a <p> tag, which cannot have nested divs.
        return (
            <View tag="span" style={styles.usernameDescription}>
                <View tag="span">{t`Your username will appear in your Khan Academy address.`}</View>
                <View tag="span" style={styles.profileUrl}>
                    {profileUrl}
                    <View tag="span" style={styles.username}>
                        {username}
                    </View>
                </View>
            </View>
        );
    };

    const privacyIndicator = (
        <div>
            <PhosphorIcon icon={globeSimpleIcon} />
            <span> {t`Anyone can see your username, avatar, and bio.`}</span>
        </div>
    );

    const content = (
        <View style={styles.content}>
            <LabeledField
                label={t`Nickname`}
                description={t`This is how your name will appear around Khan Academy, and how your friends and coaches will recognize you.`}
                field={<TextField value={nickname} onChange={setNickname} />}
                errorMessage={nicknameError}
            />
            <UsernameTextField
                testId="settings-username"
                id="username"
                label={t`Username`}
                description={renderUsernameDescription()}
                value={username || ""}
                onChange={setUsername}
                disabled={false}
            />
            {usernameError && <View style={styles.error}>{usernameError}</View>}
            <LabeledField
                label={t`Bio`}
                field={
                    <TextArea
                        value={bio}
                        onChange={setBio}
                        placeholder={t`Tell the Khan Academy community about yourself in ${maxChars} characters or less.  Who are you? How are you using Khan Academy?`}
                        rows={3}
                    />
                }
            />
            {privacyIndicator}
        </View>
    );

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const result = await updateProfile({
                nickname,
                username,
                bio,
            });

            if (result.status === "success") {
                const errors = result.data.setSettings?.errors || [];
                const errorCodes = errors.map((e) => e?.code);

                if (errorCodes.length > 0) {
                    if (errorCodes.includes("NICKNAME_ERROR")) {
                        setNicknameError(t`Please pick a different name.`);
                    }
                    if (errorCodes.includes("USERNAME_ERROR")) {
                        setUsernameError(t`That username is unavailable.`);
                    }
                    return;
                }

                reload();
                setSavedSuccessfully(true);
            }
        } catch (e) {
            throw new KAError("Failed to update profile", Errors.Internal, {
                cause: e as Error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <OnePaneDialog
            style={styles.dialog}
            title={t`Edit Profile`}
            content={content}
            footer={
                <View style={styles.footer}>
                    <Button kind="secondary" onClick={props.closeModal}>
                        {t`Cancel`}
                    </Button>
                    <Button
                        kind="primary"
                        onClick={handleSave}
                        disabled={
                            !hasChanges ||
                            usernameError !== null ||
                            nicknameError !== null ||
                            isLoading ||
                            savedSuccessfully
                        }
                        spinner={isLoading}
                    >
                        {savedSuccessfully ? t`Changes saved` : t`Save changes`}
                    </Button>
                </View>
            }
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        height: "fit-content",
        maxWidth: 640,
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        gap: sizing.size_160,
        [breakpoint.mediaQuery.xsOrSmaller]: {
            width: "100%",
            flexDirection: "column",
        },
    },
    content: {
        gap: sizing.size_240,
    },
    usernameDescription: {
        display: "flex",
        flexDirection: "column",
    },
    profileUrl: {
        display: "flex",
        flexDirection: "row",
    },
    username: {
        color: color.darkBlue,
    },
    error: {
        color: semanticColor.status.critical.foreground,
        marginInlineStart: sizing.size_160,
    },
});
